import Input from "Components/Common/Input/Input";
import DateSelect from "Components/Common/DateSelect/DateSelect";
import SignaturePad, {
  SignaturePadHandle,
} from "Components/Common/SignaturePad/SignaturePad";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { uploadFileToS3 } from "../../GcPreconCommercial/UploadFileToS3";
import Button from "Components/Common/Button/Button";

interface SignatureSectionProps {
  onSigChange: (hasSig: boolean) => void;
  onDesignationChange?: (hasDesignation: boolean) => void;
}

export interface SignatureSectionHandle {
  getSubmitData: () => Promise<{
    dataUrl: string;
    fullName: string;
    designation: string;
    companyName: string;
  }>;
  prefill: (data: {
    fullName?: string;
    designation?: string;
    companyName?: string;
    gcSignatureUrl?: string;
    signedAt?: string | null;
  }) => void;
}

interface SigFields {
  name: string;
  title: string;
  company: string;
  date: string;
}

const SignatureSection = forwardRef<
  SignatureSectionHandle,
  SignatureSectionProps
>(({ onSigChange, onDesignationChange }, ref) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const padRef = useRef<SignaturePadHandle>(null);
  const [hasSig, setHasSig] = useState<boolean>(false);
  const [sigUploading, setSigUploading] = useState<boolean>(false);
  const [fields, setFields] = useState<SigFields>({
    name: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(),
    title: "",
    company: user?.companyName || "",
    date: new Date().toISOString().split("T")[0],
  });

  const handleSigChange = (val: boolean) => {
    setHasSig(val);
    onSigChange?.(val);
  };

  const handleFieldChange = (key: keyof SigFields, value: string) => {
    setFields((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "title") {
        onDesignationChange?.(!!value.trim());
      }
      return updated;
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      getSubmitData: async () => {
        const base = {
          dataUrl: "",
          fullName: fields.name,
          designation: fields.title,
          companyName: fields.company,
        };
        if (!padRef.current?.isSigned()) return base;

        const blob = await padRef.current.getBlob();
        const file = new File([blob], "signature.png", { type: "image/png" });

        setSigUploading(true);
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result: any = await uploadFileToS3(file);
          const s3Key: string =
            result?.key ??
            result?.Key ??
            result?.data?.key ??
            result?.data?.Key ??
            "";
          return { ...base, dataUrl: s3Key };
        } finally {
          setSigUploading(false);
        }
      },
      prefill: (data) => {
        const hasDesignation = !!(data.designation && data.designation.trim());
        onDesignationChange?.(hasDesignation);

        setFields((prev) => ({
          ...prev,
          ...(data.fullName ? { name: data.fullName } : {}),
          ...(data.designation ? { title: data.designation } : {}),
          ...(data.companyName ? { company: data.companyName } : {}),
          ...(data.signedAt ? { date: data.signedAt.split("T")[0] } : {}),
        }));

        if (data.gcSignatureUrl) {
          padRef.current?.drawFromUrl(data.gcSignatureUrl);
        }
      },
    }),
    [fields, onDesignationChange],
  );

  return (
    <div className="sigSection">
      <div className="sigSection__title">GC Acknowledgment &amp; Signature</div>
      <p className="sigSection__sub">
        By signing below, I — as an authorized representative of the General
        Contractor — confirm that I have read, understood, and agree to comply
        with all project requirements listed above for the duration of this
        construction project. I understand that non-compliance may result in
        delayed or withheld disbursements.
      </p>

      <div className="sigSection__fields">
        <div className="sigSection__field">
          <label>
            Full Legal Name <span className="sigSection__req">*</span>
          </label>
          <Input
            className="sigSection__input"
            type="text"
            placeholder="Your full name"
            value={fields.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange("name", e.target.value)
            }
          />
        </div>
        <div className="sigSection__field">
          <label>
            Title / Role <span className="sigSection__req">*</span>
          </label>
          <Input
            className="sigSection__input"
            type="text"
            placeholder="e.g. President, Project Manager"
            value={fields.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange("title", e.target.value)
            }
          />
        </div>
        <div className="sigSection__field">
          <label>
            Company <span className="sigSection__req">*</span>
          </label>
          <Input
            className="sigSection__input"
            type="text"
            placeholder="Your company name"
            value={fields.company}
            readOnly
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange("company", e.target.value)
            }
          />
        </div>
        <div className="sigSection__field">
          <label>Date</label>
          <DateSelect
            value={fields.date}
            onChange={(val) => handleFieldChange("date", val)}
          />
        </div>
      </div>

      <div className="sigSection__canvasLabel">
        Signature <span className="sigSection__req">*</span>
      </div>
      <SignaturePad ref={padRef} onSignedChange={handleSigChange} />

      <div className="sigSection__actions">
        <Button
          className="sigSection__clearBtn"
          type="button"
          onClick={() => padRef.current?.clear()}
        >
          Clear
        </Button>
        <span
          className={`sigSection__status${hasSig ? " sigSection__status--done" : ""}`}
        >
          {sigUploading
            ? "Uploading signature…"
            : hasSig
              ? "✓ Signature captured"
              : "Not yet signed"}
        </span>
      </div>
    </div>
  );
});

export default SignatureSection;
